import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatCurrency as formatMoney } from '../utils/currency';

export default function DebtCollectionDashboard({ authAxios, showFormMessage, setActiveTab, setNavigateTo }) {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [hideClosed, setHideClosed] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [summaryTab, setSummaryTab] = useState('status');

  const [createOpen, setCreateOpen] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [debtors, setDebtors] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffUsersLoading, setStaffUsersLoading] = useState(false);
  const [staffUsersError, setStaffUsersError] = useState(null);

  const [casesLoading, setCasesLoading] = useState(false);
  const [casesError, setCasesError] = useState(null);

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: '',
    method: 'BANK_TRANSFER',
    reference: '',
  });

  const [debtorModalOpen, setDebtorModalOpen] = useState(false);
  const [debtorLoading, setDebtorLoading] = useState(false);
  const [debtorSaving, setDebtorSaving] = useState(false);
  const [debtorError, setDebtorError] = useState(null);
  const [debtorRecord, setDebtorRecord] = useState(null);
  const [debtorEdit, setDebtorEdit] = useState({
    is_organization: false,
    name: '',
    registration_number: '',
    national_id: '',
    phone_number: '',
    email: '',
    address: '',
  });

  const [proofHelpOpen, setProofHelpOpen] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRequest, setDetailRequest] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailData, setDetailData] = useState({
    payments: [],
    feeEntries: [],
    logs: [],
    tasks: [],
    documents: [],
  });

  const [creating, setCreating] = useState(false);
  const [createUseNewDebtor, setCreateUseNewDebtor] = useState(false);
  const [createErrors, setCreateErrors] = useState({});

  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [form, setForm] = useState({
    requesting_client: '',
    case: '',
    debtor: '',
    assigned_to: '',
    title: '',
    description: '',
    principal_amount: '',
    currency: 'BWP',
    fee_mode: 'CONTINGENCY_PERCENT',
    contingency_percent: '10.00',
    fixed_fee_amount: '0.00',
    status: 'INTAKE',
    opened_date: '',
  });

  const [newDebtor, setNewDebtor] = useState({
    is_organization: false,
    name: '',
    registration_number: '',
    national_id: '',
    phone_number: '',
    email: '',
    address: '',
  });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, r] = await Promise.all([
        authAxios.get('debt-recovery-requests/metrics/'),
        authAxios.get('debt-recovery-requests/?page_size=200'),
      ]);
      setMetrics(m.data);
      setRows(Array.isArray(r.data?.results) ? r.data.results : Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      console.error('Debt dashboard fetch error:', e);
      setError(e?.response?.data?.detail || 'Failed to load debt dashboard.');
      setMetrics(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const fetchLookups = useCallback(async () => {
    setLookupLoading(true);
    setStaffUsersLoading(true);
    setStaffUsersError(null);
    try {
      const [cResp, dResp, uResp] = await Promise.all([
        authAxios.get('clients/?page_size=200'),
        authAxios.get('debt-recovery-debtors/?page_size=200'),
        authAxios.get('users/?page_size=200'),
      ]);

      const clientRows = Array.isArray(cResp.data?.results) ? cResp.data.results : Array.isArray(cResp.data) ? cResp.data : [];
      const debtorRows = Array.isArray(dResp.data?.results) ? dResp.data.results : Array.isArray(dResp.data) ? dResp.data : [];
      const userRows = Array.isArray(uResp.data?.results) ? uResp.data.results : Array.isArray(uResp.data) ? uResp.data : [];

      setClients(clientRows);
      setCases([]);
      setDebtors(debtorRows);
      setStaffUsers(userRows);
    } catch (e) {
      console.error('Debt dashboard lookup fetch error:', e);
      showFormMessage?.('danger', 'Failed to load lookup lists (clients/cases/debtors).');
      setStaffUsersError('Failed to load staff users.');
    } finally {
      setLookupLoading(false);
      setStaffUsersLoading(false);
    }
  }, [authAxios, showFormMessage]);

  const fetchCasesForClient = useCallback(async (clientId) => {
    if (!clientId) {
      setCases([]);
      return;
    }
    setCasesLoading(true);
    setCasesError(null);
    try {
      const resp = await authAxios.get(`case-list-for-client/${clientId}/`);
      const caseRows = Array.isArray(resp.data?.results) ? resp.data.results : Array.isArray(resp.data) ? resp.data : [];
      setCases(caseRows);
    } catch (e) {
      console.error('Failed to load cases for client (endpoint), falling back to client-side filtering:', clientId, e);
      try {
        const resp = await authAxios.get('cases/?page_size=200');
        const allRows = Array.isArray(resp.data?.results) ? resp.data.results : Array.isArray(resp.data) ? resp.data : [];
        const numericId = Number(clientId);
        const filtered = allRows.filter((cs) => {
          const cid = cs.client || cs.client_id || cs.requesting_client;
          return String(cid) === String(clientId) || (Number.isFinite(numericId) && Number(cid) === numericId);
        });
        setCases(filtered);
      } catch (fallbackErr) {
        console.error('Fallback cases fetch failed:', fallbackErr);
        setCases([]);
        setCasesError('Failed to load cases for selected client.');
      }
    } finally {
      setCasesLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    if (createOpen) {
      fetchLookups();
    }
  }, [createOpen, fetchLookups]);

  useEffect(() => {
    if (editOpen && staffUsers.length === 0) {
      fetchLookups();
    }
  }, [editOpen, fetchLookups, staffUsers.length]);

  useEffect(() => {
    if (!createOpen) return;
    if (!form.requesting_client) {
      setCases([]);
      return;
    }
    fetchCasesForClient(form.requesting_client);
  }, [createOpen, fetchCasesForClient, form.requesting_client]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredRows = useMemo(() => {
    if (!hideClosed) return rows;
    return rows.filter(r => !['CLOSED', 'WRITTEN_OFF'].includes(String(r.status || '')));
  }, [rows, hideClosed]);

  const openPaymentModal = useCallback((request) => {
    if (!request) return;
    setPaymentRequest(request);
    setPaymentError(null);
    setPaymentForm({
      amount: '',
      payment_date: new Date().toISOString().split('T')[0],
      method: 'BANK_TRANSFER',
      reference: '',
    });
    setPaymentModalOpen(true);
  }, []);

  const submitPayment = useCallback(async () => {
    if (!paymentRequest?.id) return;
    const amountNum = Number(paymentForm.amount);
    if (!paymentForm.amount || Number.isNaN(amountNum) || amountNum <= 0) {
      setPaymentError('Amount must be a number greater than 0.');
      return;
    }

    setPaymentSaving(true);
    setPaymentError(null);
    try {
      await authAxios.post(`debt-recovery-requests/${paymentRequest.id}/record-payment/`, {
        amount: String(paymentForm.amount),
        payment_date: paymentForm.payment_date || null,
        method: paymentForm.method || 'BANK_TRANSFER',
        reference: paymentForm.reference || null,
      });
      showFormMessage?.('success', 'Recovery payment recorded.');
      setPaymentModalOpen(false);
      setPaymentRequest(null);
      fetchAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to record recovery payment.';
      setPaymentError(msg);
      showFormMessage?.('danger', msg);
    } finally {
      setPaymentSaving(false);
    }
  }, [authAxios, fetchAll, paymentForm, paymentRequest, showFormMessage]);

  const openDebtorModal = useCallback(async (debtorId) => {
    if (!debtorId) return;
    setDebtorModalOpen(true);
    setDebtorLoading(true);
    setDebtorSaving(false);
    setDebtorError(null);
    try {
      const resp = await authAxios.get(`debt-recovery-debtors/${debtorId}/`);
      const d = resp.data;
      setDebtorRecord(d);
      setDebtorEdit({
        is_organization: !!d.is_organization,
        name: d.name || '',
        registration_number: d.registration_number || '',
        national_id: d.national_id || '',
        phone_number: d.phone_number || '',
        email: d.email || '',
        address: d.address || '',
      });
    } catch (e) {
      console.error('Failed to load debtor:', debtorId, e);
      setDebtorRecord(null);
      setDebtorError('Failed to load debtor details.');
    } finally {
      setDebtorLoading(false);
    }
  }, [authAxios]);

  const saveDebtor = useCallback(async () => {
    if (!debtorRecord?.id) return;
    if (!debtorEdit.name?.trim()) {
      setDebtorError('Debtor name is required.');
      return;
    }
    setDebtorSaving(true);
    setDebtorError(null);
    try {
      const payload = {
        is_organization: !!debtorEdit.is_organization,
        name: String(debtorEdit.name || '').trim(),
        registration_number: debtorEdit.registration_number || null,
        national_id: debtorEdit.national_id || null,
        phone_number: debtorEdit.phone_number || null,
        email: debtorEdit.email || null,
        address: debtorEdit.address || null,
      };
      const resp = await authAxios.patch(`debt-recovery-debtors/${debtorRecord.id}/`, payload);
      const updated = resp.data;
      setDebtorRecord(updated);
      setDebtors((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      showFormMessage?.('success', 'Debtor updated.');
    } catch (e) {
      console.error('Failed to update debtor:', e);
      const msg = e?.response?.data?.detail || 'Failed to update debtor.';
      setDebtorError(msg);
      showFormMessage?.('danger', msg);
    } finally {
      setDebtorSaving(false);
    }
  }, [authAxios, debtorEdit, debtorRecord, showFormMessage]);

  const buildStatusGuidance = useCallback((req, nextStatus) => {
    const ref = req?.reference || `DR-${req?.id}`;
    const base = {
      title: `Status update: ${nextStatus}`,
      description: `Capture the required evidence for ${ref} before the status change is applied.`,
      actions: [],
      log_type: 'Note',
      subject: `Debt recovery status update: ${ref} → ${nextStatus}`,
      body: `Status change request for ${ref}.`,
    };
    switch (nextStatus) {
      case 'INTAKE':
        return {
          ...base,
          log_type: 'Letter',
          actions: ['draft_document', 'use_template'],
          description: 'Draft or select a mandate letter template and log the intake communication.',
          body: `Mandate letter drafted/selected for ${ref}.`,
        };
      case 'DEMAND_SENT':
        return {
          ...base,
          log_type: 'Letter',
          actions: ['draft_document', 'use_template', 'sms_placeholder', 'upload_proof'],
          description: 'Draft or select a demand letter, optionally log an SMS, and upload proof of delivery.',
          body: `Demand letter prepared and sent for ${ref}.`,
        };
      case 'NEGOTIATION':
        return {
          ...base,
          log_type: 'Call',
          actions: ['use_template', 'upload_proof'],
          description: 'Log negotiation call/meeting, upload minutes, and consider drafting a settlement document.',
          body: `Negotiation activity recorded for ${ref}.`,
        };
      case 'LITIGATION':
        return {
          ...base,
          log_type: 'Note',
          actions: ['create_case', 'use_template'],
          description: 'Open a new litigation case and capture supporting documents.',
          body: `Litigation decision recorded for ${ref}.`,
        };
      case 'SETTLEMENT':
        return {
          ...base,
          log_type: 'Note',
          actions: ['use_template', 'upload_proof'],
          description: 'Upload signed agreement or log settlement terms and payment schedule.',
          body: `Settlement terms recorded for ${ref}.`,
        };
      case 'PARTIALLY_RECOVERED':
        return {
          ...base,
          log_type: 'Note',
          description: 'Log commitments and next follow-up date for partial recovery.',
          body: `Partial recovery recorded for ${ref}.`,
        };
      case 'WRITTEN_OFF':
      case 'CLOSED':
        return {
          ...base,
          log_type: 'Note',
          description: 'Provide a closure or write-off reason and any final proof artifacts.',
          body: `Closure reason captured for ${ref}.`,
        };
      default:
        return base;
    }
  }, []);

  const onUpdateStatus = useCallback((requestRow, nextStatus) => {
    if (!requestRow?.id) return;
    const guidance = buildStatusGuidance(requestRow, nextStatus);
    const clientId = requestRow.requesting_client || requestRow.requesting_client_id || '';
    const caseId = requestRow.case || requestRow.case_id || '';

    const statusGuidance = {
      ...guidance,
      requestId: requestRow.id,
      requestReference: requestRow.reference || `DR-${requestRow.id}`,
      clientId,
      caseId,
    };

    const prefillLog = {
      client: clientId,
      case: caseId,
      debt_recovery_request: requestRow.id,
      debt_recovery_request_reference: statusGuidance.requestReference,
      log_type: guidance.log_type,
      subject: guidance.subject,
      body: guidance.body,
    };

    setActiveTab?.('communicationLogs');
    setNavigateTo?.({
      tab: 'communicationLogs',
      debtRecoveryRequestReference: statusGuidance.requestReference,
      prefillLog,
      statusChange: { requestId: requestRow.id, nextStatus, requestReference: statusGuidance.requestReference },
      statusGuidance,
    });
  }, [buildStatusGuidance, setActiveTab, setNavigateTo]);

  const onViewRequestLogs = useCallback((requestRowOrId) => {
    const requestId = typeof requestRowOrId === 'object' ? requestRowOrId?.id : requestRowOrId;
    if (!requestId) return;
    const reference = typeof requestRowOrId === 'object'
      ? requestRowOrId?.reference
      : rows.find((row) => String(row.id) === String(requestId))?.reference;
    if (typeof setActiveTab === 'function') {
      setActiveTab('communicationLogs');
    }
    if (typeof setNavigateTo === 'function') {
      setNavigateTo({ tab: 'communicationLogs', type: 'debt_recovery_logs', debtRecoveryRequestReference: reference || detailRequest?.reference || `DR-${requestId}` });
    }
  }, [detailRequest, rows, setActiveTab, setNavigateTo]);

  const openDocumentsForDrr = useCallback((mode, req) => {
    if (!req?.id) return;
    if (typeof setActiveTab === 'function') {
      setActiveTab('documents');
    }
    if (typeof setNavigateTo === 'function') {
      setNavigateTo({
        tab: 'documents',
        type: 'drr_document',
        mode,
        debtRecoveryRequestId: req.id,
        clientId: req.requesting_client || req.requesting_client_id,
        caseId: req.case || req.case_id,
      });
    }
  }, [setActiveTab, setNavigateTo]);

  const createCaseFromDrr = useCallback(async (req) => {
    if (!req?.id) return;
    try {
      const resp = await authAxios.post(`debt-recovery-requests/${req.id}/create-case/`, {});
      showFormMessage?.('success', 'Case created from debt recovery request.');
      const caseId = resp?.data?.id;
      if (caseId && typeof setActiveTab === 'function') {
        setActiveTab('cases');
        setNavigateTo?.({ tab: 'cases', id: caseId, type: 'case_open', autoOpen: true });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to create case from debt recovery request.';
      showFormMessage?.('danger', msg);
    }
  }, [authAxios, setActiveTab, setNavigateTo, showFormMessage]);

  const normalizeResults = useCallback((resp) => {
    if (!resp) return [];
    const data = resp.data || resp;
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  }, []);

  const openDetailModal = useCallback(async (req) => {
    if (!req) return;
    setDetailRequest(req);
    setDetailTab('overview');
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    try {
      const [paymentsResp, feeResp, logsResp, tasksResp, docsResp] = await Promise.all([
        authAxios.get(`debt-recovery-payments/?request=${req.id}&page_size=200`),
        authAxios.get(`debt-recovery-fee-entries/?debt_recovery_request=${req.id}&page_size=200`),
        authAxios.get(`communication-logs/?debt_recovery_request=${req.id}&page_size=200`),
        authAxios.get(`tasks/?debt_recovery_request=${req.id}&page_size=200`),
        req.case ? authAxios.get(`documents/?case=${req.case}&page_size=200`) : authAxios.get(`documents/?client=${req.requesting_client}&page_size=200`),
      ]);
      setDetailData({
        payments: normalizeResults(paymentsResp),
        feeEntries: normalizeResults(feeResp),
        logs: normalizeResults(logsResp),
        tasks: normalizeResults(tasksResp),
        documents: normalizeResults(docsResp),
      });
    } catch (e) {
      console.error('Failed to load debt recovery detail data', e);
      setDetailError('Failed to load debt recovery request details.');
    } finally {
      setDetailLoading(false);
    }
  }, [authAxios, normalizeResults]);

  const closeDetailModal = useCallback(() => {
    setDetailOpen(false);
    setDetailRequest(null);
    setDetailData({ payments: [], feeEntries: [], logs: [], tasks: [], documents: [] });
    setDetailError(null);
  }, []);

  const statusEntries = useMemo(() => Object.entries(metrics?.by_status || {}), [metrics?.by_status]);
  const totalOpenCount = useMemo(() => statusEntries.reduce((sum, [, v]) => sum + Number(v || 0), 0), [statusEntries]);
  const totalToRecover = Number(metrics?.total_to_recover || 0);
  const totalRecovered = Number(metrics?.total_recovered || 0);
  const totalOutstanding = Number(metrics?.total_outstanding || 0);
  const recoveryPct = totalToRecover > 0 ? Math.min(100, (totalRecovered / totalToRecover) * 100) : 0;

  const formatClientName = useCallback((c) => {
    if (!c) return '';
    const org = c.organization_name;
    if (org) return org;
    const fn = c.first_name || '';
    const ln = c.last_name || '';
    const s = `${fn} ${ln}`.trim();
    return s || `Client #${c.id}`;
  }, []);

  const resetCreateForm = useCallback(() => {
    setCreateErrors({});
    setCreateUseNewDebtor(false);
    setForm({
      requesting_client: '',
      case: '',
      debtor: '',
      assigned_to: '',
      title: '',
      description: '',
      principal_amount: '',
      currency: 'BWP',
      fee_mode: 'CONTINGENCY_PERCENT',
      contingency_percent: '10.00',
      fixed_fee_amount: '0.00',
      status: 'INTAKE',
      opened_date: '',
    });
    setNewDebtor({
      is_organization: false,
      name: '',
      registration_number: '',
      national_id: '',
      phone_number: '',
      email: '',
      address: '',
    });
  }, []);

  const openEditModal = useCallback((req) => {
    if (!req) return;
    setEditError(null);
    setEditForm({
      id: req.id,
      reference: req.reference,
      requesting_client: req.requesting_client || req.requesting_client_id || '',
      case: req.case || req.case_id || '',
      debtor: req.debtor?.id || req.debtor || '',
      assigned_to: req.assigned_to || req.assigned_to_id || '',
      title: req.title || '',
      description: req.description || '',
      principal_amount: req.principal_amount || '',
      currency: req.currency || 'BWP',
      fee_mode: req.fee_mode || 'CONTINGENCY_PERCENT',
      contingency_percent: req.contingency_percent || '0.00',
      fixed_fee_amount: req.fixed_fee_amount || '0.00',
      status: req.status || 'INTAKE',
      opened_date: req.opened_date || '',
    });
    if (req.requesting_client || req.requesting_client_id) {
      fetchCasesForClient(req.requesting_client || req.requesting_client_id);
    }
    setEditOpen(true);
  }, [fetchCasesForClient]);

  const closeEditModal = useCallback(() => {
    setEditOpen(false);
    setEditForm(null);
    setEditError(null);
  }, []);

  const submitEdit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!editForm?.id) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const payload = {
        requesting_client: editForm.requesting_client || null,
        case: editForm.case || null,
        debtor: editForm.debtor || null,
        assigned_to: editForm.assigned_to || null,
        title: String(editForm.title || '').trim(),
        description: editForm.description || null,
        principal_amount: String(editForm.principal_amount || '0.00'),
        currency: editForm.currency || 'BWP',
        fee_mode: editForm.fee_mode || 'CONTINGENCY_PERCENT',
        contingency_percent: String(editForm.contingency_percent || '0.00'),
        fixed_fee_amount: String(editForm.fixed_fee_amount || '0.00'),
        status: editForm.status || 'INTAKE',
        opened_date: editForm.opened_date || null,
      };
      await authAxios.patch(`debt-recovery-requests/${editForm.id}/`, payload);
      showFormMessage?.('success', 'Debt recovery request updated.');
      closeEditModal();
      fetchAll();
    } catch (e) {
      console.error('Failed to update debt recovery request', e);
      const msg = e?.response?.data?.detail || 'Failed to update debt recovery request.';
      setEditError(msg);
      showFormMessage?.('danger', msg);
    } finally {
      setEditSaving(false);
    }
  }, [authAxios, closeEditModal, editForm, fetchAll, showFormMessage]);

  const openDeleteModal = useCallback((req) => {
    if (!req) return;
    setDeleteTarget(req);
    setDeleteOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteOpen(false);
    setDeleteTarget(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget?.id) return;
    setDeleteLoading(true);
    try {
      await authAxios.delete(`debt-recovery-requests/${deleteTarget.id}/`);
      showFormMessage?.('success', 'Debt recovery request deleted.');
      closeDeleteModal();
      fetchAll();
    } catch (e) {
      console.error('Failed to delete debt recovery request', e);
      const msg = e?.response?.data?.detail || 'Failed to delete debt recovery request.';
      showFormMessage?.('danger', msg);
    } finally {
      setDeleteLoading(false);
    }
  }, [authAxios, closeDeleteModal, deleteTarget, fetchAll, showFormMessage]);

  const inputClass = 'mt-1 w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm';
  const inputErrorClass = 'mt-1 w-full px-3 py-2 bg-white border border-red-400 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm';
  const btnPrimary = 'px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50';
  const btnSecondary = 'px-4 py-2 rounded-md bg-white border border-gray-300 text-gray-800 hover:bg-gray-50 disabled:opacity-50';

  const validateCreate = useCallback(() => {
    const nextErrors = {};
    if (!form.requesting_client) nextErrors.requesting_client = 'Client is required.';
    if (!form.title?.trim()) nextErrors.title = 'Title is required.';

    const principalNum = Number(form.principal_amount);
    if (!form.principal_amount || Number.isNaN(principalNum) || principalNum <= 0) {
      nextErrors.principal_amount = 'Principal amount must be a number greater than 0.';
    }

    if (createUseNewDebtor) {
      if (!newDebtor.name?.trim()) nextErrors.debtor_name = 'Debtor name is required.';
    } else {
      if (!form.debtor) nextErrors.debtor = 'Debtor is required.';
    }

    if (form.fee_mode === 'CONTINGENCY_PERCENT') {
      const pct = Number(form.contingency_percent);
      if (Number.isNaN(pct) || pct < 0) nextErrors.contingency_percent = 'Contingency percent must be 0 or more.';
    }
    if (form.fee_mode === 'FIXED_AMOUNT') {
      const fixed = Number(form.fixed_fee_amount);
      if (Number.isNaN(fixed) || fixed < 0) nextErrors.fixed_fee_amount = 'Fixed fee amount must be 0 or more.';
    }

    setCreateErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [createUseNewDebtor, form, newDebtor]);

  const onSubmitCreate = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!validateCreate()) {
      showFormMessage?.('danger', 'Please fix the form errors.');
      return;
    }

    setCreating(true);
    try {
      let debtorId = form.debtor;
      if (createUseNewDebtor) {
        const payload = {
          is_organization: !!newDebtor.is_organization,
          name: String(newDebtor.name || '').trim(),
          registration_number: newDebtor.registration_number || null,
          national_id: newDebtor.national_id || null,
          phone_number: newDebtor.phone_number || null,
          email: newDebtor.email || null,
          address: newDebtor.address || null,
        };
        const resp = await authAxios.post('debt-recovery-debtors/', payload);
        debtorId = resp.data?.id;
        if (!debtorId) {
          throw new Error('Debtor create failed (missing id).');
        }
      }

      const reqPayload = {
        requesting_client: form.requesting_client,
        debtor: debtorId,
        title: String(form.title || '').trim(),
        description: form.description || null,
        principal_amount: String(form.principal_amount),
        currency: form.currency || 'BWP',
        fee_mode: form.fee_mode,
        contingency_percent: String(form.contingency_percent || '0.00'),
        fixed_fee_amount: String(form.fixed_fee_amount || '0.00'),
        status: form.status || 'INTAKE',
      };
      if (form.case) reqPayload.case = form.case;
      if (form.assigned_to) reqPayload.assigned_to = form.assigned_to;
      if (form.opened_date) reqPayload.opened_date = form.opened_date;

      const created = await authAxios.post('debt-recovery-requests/', reqPayload);

      const createdRef = created.data?.reference || `DR-${created.data?.id || ''}`;
      showFormMessage?.('success', `Created recovery request ${createdRef}.`);

      resetCreateForm();
      setCreateOpen(false);
      fetchAll();
    } catch (err) {
      console.error('Create debt recovery request error:', err);
      const data = err?.response?.data;
      const msg = data?.detail || 'Failed to create recovery request.';
      showFormMessage?.('danger', msg);
      if (data && typeof data === 'object' && !data.detail) {
        setCreateErrors((prev) => ({ ...prev, api: data }));
      }
    } finally {
      setCreating(false);
    }
  }, [authAxios, createUseNewDebtor, fetchAll, form, newDebtor, resetCreateForm, showFormMessage, validateCreate]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-800">Debt Recovery Dashboard</h2>
          <p className="text-gray-600">Manage client debt recovery mandates (separate from firm invoices).</p>
          <p className="text-xs text-gray-500 mt-1">Recording a recovery payment automatically creates unbilled fee activity (WIP) for Billing (where applicable).</p>
        </div>
        <div className="flex gap-2">
          <button
            className={btnSecondary}
            onClick={() => {
              const next = !createOpen;
              setCreateOpen(next);
              if (!next) resetCreateForm();
            }}
            disabled={loading}
          >
            {createOpen ? 'Close Form' : 'Create Recovery Request'}
          </button>
          <button
            className={btnPrimary}
            onClick={fetchAll}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="mb-6 bg-white rounded-lg shadow-md border border-gray-100">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">Action Proof Checklist</h3>
            <p className="text-sm text-gray-600">Capture comms + artifacts for every status transition.</p>
          </div>
          <button className="text-sm text-indigo-600 hover:underline" onClick={() => setProofHelpOpen(v => !v)}>
            {proofHelpOpen ? 'Hide' : 'Show'}
          </button>
        </div>
        {proofHelpOpen && (
          <div className="p-4 text-sm text-gray-700 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { label: 'INTAKE', text: 'Mandate letter + debtor KYC + initial notes.' },
              { label: 'DEMAND_SENT', text: 'Demand letter/SMS + proof of delivery.' },
              { label: 'NEGOTIATION', text: 'Minutes/settlement drafts + call summary.' },
              { label: 'LITIGATION', text: 'Court filings, receipts, hearing dates.' },
              { label: 'SETTLEMENT', text: 'Signed agreement + payment schedule.' },
              { label: 'PARTIALLY_RECOVERED', text: 'Each payment + next follow-up date.' },
              { label: 'RECOVERED / WRITTEN_OFF / CLOSED', text: 'Closure reason + final proof artifact.' },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3">
                <span className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{item.label}</span>
                <div className="text-gray-700">{item.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {createOpen && (
        <div className="bg-white rounded-lg shadow-md mb-6 border border-gray-100">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">New Debt Recovery Request</h3>
            <div className="text-sm text-gray-500">Reference is auto-generated.</div>
          </div>
          <form className="p-4" onSubmit={onSubmitCreate}>
            {lookupLoading && (
              <div className="mb-3 text-sm text-gray-600">Loading clients/debtors…</div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Requesting Client *</label>
                <select
                  className={createErrors.requesting_client ? inputErrorClass : inputClass}
                  value={form.requesting_client}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm((prev) => ({ ...prev, requesting_client: v, case: '' }));
                  }}
                >
                  <option value="">Select client…</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{formatClientName(c)}</option>
                  ))}
                </select>
                {createErrors.requesting_client && <div className="text-xs text-red-600 mt-1">{createErrors.requesting_client}</div>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Case (optional)</label>
                <select
                  className={inputClass}
                  value={form.case}
                  onChange={(e) => setForm((prev) => ({ ...prev, case: e.target.value }))}
                  disabled={!form.requesting_client || casesLoading}
                >
                  <option value="">{form.requesting_client ? 'No case' : 'Select a client first'}</option>
                  {cases.map((cs) => (
                    <option key={cs.id} value={cs.id}>{cs.title || `Case #${cs.id}`}</option>
                  ))}
                </select>
                {casesLoading && <div className="text-xs text-gray-500 mt-1">Loading client cases…</div>}
                {casesError && <div className="text-xs text-red-600 mt-1">{casesError}</div>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Assigned To (optional)</label>
                <select
                  className={inputClass}
                  value={form.assigned_to}
                  onChange={(e) => setForm((prev) => ({ ...prev, assigned_to: e.target.value }))}
                  disabled={staffUsersLoading}
                >
                  <option value="">Unassigned</option>
                  {staffUsers.map((u) => (
                    <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username}</option>
                  ))}
                </select>
                {staffUsersLoading && <div className="text-xs text-gray-500 mt-1">Loading staff…</div>}
                {staffUsersError && <div className="text-xs text-red-600 mt-1">{staffUsersError}</div>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Opened Date (optional)</label>
                <input
                  type="date"
                  className={inputClass}
                  value={form.opened_date}
                  onChange={(e) => setForm((prev) => ({ ...prev, opened_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">Debtor *</label>
                <label className="text-sm text-gray-700 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={createUseNewDebtor}
                    onChange={(e) => {
                      setCreateUseNewDebtor(e.target.checked);
                      setCreateErrors((prev) => ({ ...prev, debtor: undefined, debtor_name: undefined }));
                    }}
                  />
                  Create new debtor
                </label>
              </div>

              {!createUseNewDebtor ? (
                <>
                  <select
                    className={createErrors.debtor ? inputErrorClass : inputClass}
                    value={form.debtor}
                    onChange={(e) => setForm((prev) => ({ ...prev, debtor: e.target.value }))}
                  >
                    <option value="">Select debtor…</option>
                    {debtors.map((d) => (
                      <option key={d.id} value={d.id}>{d.name || `Debtor #${d.id}`}</option>
                    ))}
                  </select>
                  {createErrors.debtor && <div className="text-xs text-red-600 mt-1">{createErrors.debtor}</div>}
                  <div className="mt-2">
                    <button
                      type="button"
                      className={btnSecondary}
                      disabled={!form.debtor}
                      onClick={() => openDebtorModal(form.debtor)}
                    >
                      View / Edit Debtor
                    </button>
                  </div>
                </>
              ) : (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700">Name *</label>
                    <input
                      className={createErrors.debtor_name ? inputErrorClass : inputClass}
                      value={newDebtor.name}
                      onChange={(e) => setNewDebtor((prev) => ({ ...prev, name: e.target.value }))}
                    />
                    {createErrors.debtor_name && <div className="text-xs text-red-600 mt-1">{createErrors.debtor_name}</div>}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">Phone</label>
                    <input
                      className={inputClass}
                      value={newDebtor.phone_number}
                      onChange={(e) => setNewDebtor((prev) => ({ ...prev, phone_number: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">Email</label>
                    <input
                      type="email"
                      className={inputClass}
                      value={newDebtor.email}
                      onChange={(e) => setNewDebtor((prev) => ({ ...prev, email: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">Registration #</label>
                    <input
                      className={inputClass}
                      value={newDebtor.registration_number}
                      onChange={(e) => setNewDebtor((prev) => ({ ...prev, registration_number: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700">National ID</label>
                    <input
                      className={inputClass}
                      value={newDebtor.national_id}
                      onChange={(e) => setNewDebtor((prev) => ({ ...prev, national_id: e.target.value }))}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!newDebtor.is_organization}
                      onChange={(e) => setNewDebtor((prev) => ({ ...prev, is_organization: e.target.checked }))}
                    />
                    <div className="text-sm text-gray-700">Organization</div>
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-sm text-gray-700">Address</label>
                    <textarea
                      className={inputClass}
                      rows={2}
                      value={newDebtor.address}
                      onChange={(e) => setNewDebtor((prev) => ({ ...prev, address: e.target.value }))}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Title *</label>
                <input
                  className={createErrors.title ? inputErrorClass : inputClass}
                  value={form.title}
                  onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                />
                {createErrors.title && <div className="text-xs text-red-600 mt-1">{createErrors.title}</div>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  className={inputClass}
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  {[
                    'INTAKE','DEMAND_SENT','NEGOTIATION','LITIGATION','SETTLEMENT','PARTIALLY_RECOVERED','RECOVERED','WRITTEN_OFF','CLOSED'
                  ].map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
              <textarea
                className={inputClass}
                rows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Principal Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  className={createErrors.principal_amount ? inputErrorClass : inputClass}
                  value={form.principal_amount}
                  onChange={(e) => setForm((prev) => ({ ...prev, principal_amount: e.target.value }))}
                />
                {createErrors.principal_amount && <div className="text-xs text-red-600 mt-1">{createErrors.principal_amount}</div>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Currency</label>
                <input
                  className={inputClass}
                  value={form.currency}
                  onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Fee Mode</label>
                <select
                  className={inputClass}
                  value={form.fee_mode}
                  onChange={(e) => setForm((prev) => ({ ...prev, fee_mode: e.target.value }))}
                >
                  <option value="CONTINGENCY_PERCENT">Contingency (% of recovered)</option>
                  <option value="FIXED_AMOUNT">Fixed Amount</option>
                  <option value="NONE">None</option>
                </select>
              </div>
            </div>

            {(form.fee_mode === 'CONTINGENCY_PERCENT' || form.fee_mode === 'FIXED_AMOUNT') && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {form.fee_mode === 'CONTINGENCY_PERCENT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contingency Percent</label>
                    <input
                      type="number"
                      step="0.01"
                      className={createErrors.contingency_percent ? inputErrorClass : inputClass}
                      value={form.contingency_percent}
                      onChange={(e) => setForm((prev) => ({ ...prev, contingency_percent: e.target.value }))}
                    />
                    {createErrors.contingency_percent && <div className="text-xs text-red-600 mt-1">{createErrors.contingency_percent}</div>}
                  </div>
                )}
                {form.fee_mode === 'FIXED_AMOUNT' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Fixed Fee Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      className={createErrors.fixed_fee_amount ? inputErrorClass : inputClass}
                      value={form.fixed_fee_amount}
                      onChange={(e) => setForm((prev) => ({ ...prev, fixed_fee_amount: e.target.value }))}
                    />
                    {createErrors.fixed_fee_amount && <div className="text-xs text-red-600 mt-1">{createErrors.fixed_fee_amount}</div>}
                  </div>
                )}
              </div>
            )}

            {createErrors.api && (
              <div className="mt-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
                Backend validation: {JSON.stringify(createErrors.api)}
              </div>
            )}

            <div className="mt-6 flex items-center gap-2">
              <button
                type="submit"
                className={btnPrimary}
                disabled={creating}
              >
                {creating ? 'Creating…' : 'Create Request'}
              </button>
              <button
                type="button"
                className={btnSecondary}
                onClick={() => {
                  resetCreateForm();
                  setCreateOpen(false);
                }}
                disabled={creating}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-lg shadow-md border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white">
          <div className="text-xs uppercase tracking-wide text-indigo-500">Total To Recover</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{formatMoney(metrics?.total_to_recover)}</div>
          <div className="text-xs text-indigo-600 mt-1">Active mandates</div>
        </div>
        <div className="p-4 rounded-lg shadow-md border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white">
          <div className="text-xs uppercase tracking-wide text-emerald-500">Total Recovered</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{formatMoney(metrics?.total_recovered)}</div>
          <div className="text-xs text-emerald-600 mt-1">Collected to date</div>
        </div>
        <div className="p-4 rounded-lg shadow-md border border-amber-100 bg-gradient-to-br from-amber-50 to-white">
          <div className="text-xs uppercase tracking-wide text-amber-500">Total Outstanding</div>
          <div className="text-2xl font-semibold text-gray-900 mt-1">{formatMoney(metrics?.total_outstanding)}</div>
          <div className="text-xs text-amber-600 mt-1">Success fees due: {formatMoney(metrics?.success_fees_due)}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md mb-6 border border-gray-100">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">Portfolio Summary</h3>
            <p className="text-xs text-gray-500">Status distribution and recovery progress.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className={`px-3 py-1.5 text-sm rounded-md ${summaryTab === 'status' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setSummaryTab('status')}
            >
              Status
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 text-sm rounded-md ${summaryTab === 'recovery' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
              onClick={() => setSummaryTab('recovery')}
            >
              Recovery
            </button>
          </div>
        </div>
        <div className="p-4 overflow-x-auto">
          {summaryTab === 'status' && (
            <div className="space-y-3">
              {statusEntries.length === 0 ? (
                <div className="text-sm text-gray-500">No data.</div>
              ) : (
                statusEntries.map(([k, v]) => {
                  const count = Number(v || 0);
                  const pct = totalOpenCount > 0 ? Math.round((count / totalOpenCount) * 100) : 0;
                  return (
                    <div key={k} className="space-y-1">
                      <div className="flex items-center justify-between text-sm text-gray-700">
                        <span>{k}</span>
                        <span className="text-xs text-gray-500">{count} • {pct}%</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
          {summaryTab === 'recovery' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-700">Recovered vs outstanding</div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${recoveryPct}%` }} />
              </div>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="bg-emerald-50 border border-emerald-100 rounded px-3 py-2 text-emerald-700">
                  Recovered: {formatMoney(totalRecovered)}
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded px-3 py-2 text-amber-700">
                  Outstanding: {formatMoney(totalOutstanding)}
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded px-3 py-2 text-gray-700">
                  Total: {formatMoney(totalToRecover)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md border border-gray-100">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-800">Debt Recovery Requests</h3>
            <p className="text-xs text-gray-500">Click a request to open the detail panel.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-md border border-gray-200 overflow-hidden">
              <button
                type="button"
                className={`px-3 py-1.5 text-sm ${viewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                onClick={() => setViewMode('list')}
              >
                List
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 text-sm ${viewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                onClick={() => setViewMode('tiles')}
              >
                Tiles
              </button>
            </div>
            <label className="text-sm text-gray-700 flex items-center gap-2">
              <input
                type="checkbox"
                checked={hideClosed}
                onChange={(e) => setHideClosed(e.target.checked)}
              />
              Hide closed/written-off
            </label>
          </div>
        </div>

        <div className="p-4 overflow-x-auto">
          {loading ? (
            <div className="text-gray-600">Loading…</div>
          ) : viewMode === 'list' ? (
            <table className="min-w-full text-sm">
              <thead className="text-left text-gray-600">
                <tr>
                  <th className="py-2">Reference</th>
                  <th className="py-2">Client</th>
                  <th className="py-2">Debtor</th>
                  <th className="py-2">Principal</th>
                  <th className="py-2">Recovered</th>
                  <th className="py-2">Outstanding</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-6 text-center text-gray-500">No debt recovery requests found.</td>
                  </tr>
                ) : (
                  filteredRows.map((r) => (
                    <tr key={r.id} className="border-t cursor-pointer hover:bg-gray-50" onClick={() => openDetailModal(r)}>
                      <td className="py-2 font-medium text-gray-800">{r.reference || `DR-${r.id}`}</td>
                      <td className="py-2">
                        <div className="text-gray-800">{r.requesting_client_name || r.requesting_client}</div>
                        <div className="text-xs text-gray-500">{r.title || ''}</div>
                      </td>
                      <td className="py-2">
                        <button
                          type="button"
                          className="text-indigo-700 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDebtorModal(r.debtor?.id || r.debtor);
                          }}
                          title="View / edit debtor"
                        >
                          {r.debtor_name || r.debtor}
                        </button>
                      </td>
                      <td className="py-2">{formatMoney(r.principal_amount)}</td>
                      <td className="py-2">{formatMoney(r.total_recovered)}</td>
                      <td className="py-2">{formatMoney(r.balance_remaining)}</td>
                      <td className="py-2">
                        <select
                          className="border rounded px-2 py-1 bg-white"
                          value={r.status || 'INTAKE'}
                          onChange={(e) => onUpdateStatus(r, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {[
                            'INTAKE','DEMAND_SENT','NEGOTIATION','LITIGATION','SETTLEMENT','PARTIALLY_RECOVERED','RECOVERED','WRITTEN_OFF','CLOSED'
                          ].map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2">
                        <div className="flex gap-2">
                          <button
                            className={btnPrimary}
                            onClick={(e) => {
                              e.stopPropagation();
                              openPaymentModal(r);
                            }}
                            title="Record recovered payment"
                          >
                            Record Payment
                          </button>
                          <button
                            className={btnSecondary}
                            onClick={(e) => {
                              e.stopPropagation();
                              onViewRequestLogs(r);
                            }}
                            title="View Communication Logs for this debt recovery request"
                          >
                            View Logs
                          </button>
                          <button
                            className={btnSecondary}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditModal(r);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="px-4 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteModal(r);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredRows.length === 0 ? (
                <div className="text-center text-gray-500 col-span-full">No debt recovery requests found.</div>
              ) : (
                filteredRows.map((r) => (
                  <div key={r.id} className="border rounded-lg p-4 bg-white shadow-sm hover:shadow-md transition" onClick={() => openDetailModal(r)} role="button" tabIndex={0}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs text-gray-500">{r.reference || `DR-${r.id}`}</div>
                        <div className="text-lg font-semibold text-gray-800">{r.title || 'Untitled request'}</div>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700">{r.status}</span>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">
                      <div><span className="font-medium">Client:</span> {r.requesting_client_name || r.requesting_client}</div>
                      <div><span className="font-medium">Debtor:</span> {r.debtor_name || r.debtor}</div>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                      <div className="p-2 rounded bg-gray-50">
                        <div className="text-gray-500">Principal</div>
                        <div className="font-semibold text-gray-800">{formatMoney(r.principal_amount)}</div>
                      </div>
                      <div className="p-2 rounded bg-gray-50">
                        <div className="text-gray-500">Recovered</div>
                        <div className="font-semibold text-gray-800">{formatMoney(r.total_recovered)}</div>
                      </div>
                      <div className="p-2 rounded bg-gray-50">
                        <div className="text-gray-500">Outstanding</div>
                        <div className="font-semibold text-gray-800">{formatMoney(r.balance_remaining)}</div>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        className={btnPrimary}
                        onClick={(e) => {
                          e.stopPropagation();
                          openPaymentModal(r);
                        }}
                      >
                        Record Payment
                      </button>
                      <button
                        className={btnSecondary}
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewRequestLogs(r);
                        }}
                      >
                        View Logs
                      </button>
                      <button
                        className={btnSecondary}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditModal(r);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="px-4 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 hover:bg-red-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDeleteModal(r);
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Debt Recovery Request Detail Modal */}
      {detailOpen && detailRequest && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Debt Recovery Request</h3>
                <p className="text-xs text-gray-500">{detailRequest.reference || `DR-${detailRequest.id}`}</p>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeDetailModal}>✕</button>
            </div>
            <div className="p-4 border-b flex flex-wrap gap-2">
              {[
                { key: 'overview', label: 'Overview' },
                { key: 'payments', label: 'Payments' },
                { key: 'fees', label: 'Fees' },
                { key: 'logs', label: 'Comms' },
                { key: 'tasks', label: 'Tasks' },
                { key: 'documents', label: 'Documents' },
              ].map(tab => (
                <button
                  key={tab.key}
                  className={`px-3 py-1.5 rounded-md text-sm ${detailTab === tab.key ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  onClick={() => setDetailTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="p-4 border-b bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="rounded-lg border border-indigo-100 bg-white p-3">
                  <div className="text-xs uppercase text-indigo-500">Principal</div>
                  <div className="text-lg font-semibold">{formatMoney(detailRequest.principal_amount)}</div>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-white p-3">
                  <div className="text-xs uppercase text-emerald-500">Recovered</div>
                  <div className="text-lg font-semibold">{formatMoney(detailRequest.total_recovered)}</div>
                </div>
                <div className="rounded-lg border border-amber-100 bg-white p-3">
                  <div className="text-xs uppercase text-amber-500">Outstanding</div>
                  <div className="text-lg font-semibold">{formatMoney(detailRequest.balance_remaining)}</div>
                </div>
                <div className="rounded-lg border border-slate-100 bg-white p-3">
                  <div className="text-xs uppercase text-slate-500">Success Fee</div>
                  <div className="text-lg font-semibold">{formatMoney(detailRequest.success_fee_due || detailRequest.success_fee_amount)}</div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button className={btnSecondary} onClick={() => openDocumentsForDrr('draft', detailRequest)}>Draft Document</button>
                <button className={btnSecondary} onClick={() => openDocumentsForDrr('template', detailRequest)}>Use Template</button>
                <button className={btnSecondary} onClick={() => openDocumentsForDrr('upload', detailRequest)}>Upload Proof</button>
                <button className={btnSecondary} onClick={() => onViewRequestLogs(detailRequest)}>View Logs</button>
                <button className={btnSecondary} onClick={() => createCaseFromDrr(detailRequest)}>Create Case</button>
                <button className={btnSecondary} onClick={() => openEditModal(detailRequest)}>Edit</button>
                <button className="px-4 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 hover:bg-red-100" onClick={() => openDeleteModal(detailRequest)}>Delete</button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {detailLoading && <div className="text-gray-600">Loading details…</div>}
              {detailError && <div className="text-red-600 text-sm mb-2">{detailError}</div>}

              {!detailLoading && detailTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="font-medium">Client:</span> {detailRequest.requesting_client_name || detailRequest.requesting_client}</div>
                  <div><span className="font-medium">Debtor:</span> {detailRequest.debtor_name || detailRequest.debtor}</div>
                  <div><span className="font-medium">Title:</span> {detailRequest.title}</div>
                  <div><span className="font-medium">Status:</span> {detailRequest.status}</div>
                  <div><span className="font-medium">Principal:</span> {formatMoney(detailRequest.principal_amount)}</div>
                  <div><span className="font-medium">Recovered:</span> {formatMoney(detailRequest.total_recovered)}</div>
                  <div><span className="font-medium">Outstanding:</span> {formatMoney(detailRequest.balance_remaining)}</div>
                  <div><span className="font-medium">Fee Mode:</span> {detailRequest.fee_mode}</div>
                  <div><span className="font-medium">Contingency %:</span> {detailRequest.contingency_percent || '—'}</div>
                  <div><span className="font-medium">Fixed Fee:</span> {formatMoney(detailRequest.fixed_fee_amount)}</div>
                  <div><span className="font-medium">Opened:</span> {detailRequest.opened_date || '—'}</div>
                  <div><span className="font-medium">Assigned To:</span> {detailRequest.assigned_to_username || '—'}</div>
                </div>
              )}

              {!detailLoading && detailTab === 'payments' && (
                <div className="space-y-2">
                  {detailData.payments.length === 0 ? (
                    <div className="text-gray-500 text-sm">No payments recorded.</div>
                  ) : detailData.payments.map(p => (
                    <div key={p.id} className="border rounded p-3 text-sm">
                      <div className="font-medium">{formatMoney(p.amount)} • {p.method}</div>
                      <div className="text-gray-500">{p.payment_date}</div>
                      {p.reference && <div className="text-gray-500">Ref: {p.reference}</div>}
                    </div>
                  ))}
                </div>
              )}

              {!detailLoading && detailTab === 'fees' && (
                <div className="space-y-2">
                  {detailData.feeEntries.length === 0 ? (
                    <div className="text-gray-500 text-sm">No fee entries.</div>
                  ) : detailData.feeEntries.map(f => (
                    <div key={f.id} className="border rounded p-3 text-sm">
                      <div className="font-medium">{f.fee_type} • {formatMoney(f.amount)}</div>
                      <div className="text-gray-500">{f.description || 'Debt recovery fee'}</div>
                    </div>
                  ))}
                </div>
              )}

              {!detailLoading && detailTab === 'logs' && (
                <div className="space-y-2">
                  {detailData.logs.length === 0 ? (
                    <div className="text-gray-500 text-sm">No communication logs.</div>
                  ) : detailData.logs.map(l => (
                    <div key={l.id} className="border rounded p-3 text-sm">
                      <div className="font-medium">{l.log_type} • {l.subject}</div>
                      <div className="text-gray-500">{new Date(l.timestamp).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}

              {!detailLoading && detailTab === 'tasks' && (
                <div className="space-y-2">
                  {detailData.tasks.length === 0 ? (
                    <div className="text-gray-500 text-sm">No tasks linked to this request.</div>
                  ) : detailData.tasks.map(t => (
                    <div key={t.id} className="border rounded p-3 text-sm">
                      <div className="font-medium">{t.title}</div>
                      <div className="text-gray-500">Status: {t.status} • Priority: {t.priority}</div>
                    </div>
                  ))}
                </div>
              )}

              {!detailLoading && detailTab === 'documents' && (
                <div className="space-y-2">
                  {detailData.documents.length === 0 ? (
                    <div className="text-gray-500 text-sm">No documents found for this request.</div>
                  ) : detailData.documents.map(d => (
                    <div key={d.id} className="border rounded p-3 text-sm">
                      <div className="font-medium">{d.title}</div>
                      <div className="text-gray-500">{d.document_type || 'Document'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className={btnSecondary} onClick={closeDetailModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {editOpen && editForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Edit Debt Recovery Request</h3>
                <p className="text-xs text-gray-500">{editForm.reference || `DR-${editForm.id}`}</p>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={closeEditModal} disabled={editSaving}>✕</button>
            </div>
            <form onSubmit={submitEdit} className="p-4 space-y-4 overflow-y-auto max-h-[75vh]">
              {editError && <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{editError}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Requesting Client</label>
                  <select
                    className={inputClass}
                    value={editForm.requesting_client}
                    onChange={(e) => {
                      const v = e.target.value;
                      setEditForm((prev) => ({ ...prev, requesting_client: v, case: '' }));
                      fetchCasesForClient(v);
                    }}
                  >
                    <option value="">Select client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{formatClientName(c)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Case (optional)</label>
                  <select
                    className={inputClass}
                    value={editForm.case}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, case: e.target.value }))}
                    disabled={!editForm.requesting_client || casesLoading}
                  >
                    <option value="">No case</option>
                    {cases.map((cs) => (
                      <option key={cs.id} value={cs.id}>{cs.title || `Case #${cs.id}`}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Assigned To</label>
                  <select
                    className={inputClass}
                    value={editForm.assigned_to}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, assigned_to: e.target.value }))}
                  >
                    <option value="">Unassigned</option>
                    {staffUsers.map((u) => (
                      <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Debtor</label>
                  <select
                    className={inputClass}
                    value={editForm.debtor}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, debtor: e.target.value }))}
                  >
                    <option value="">Select debtor…</option>
                    {debtors.map((d) => (
                      <option key={d.id} value={d.id}>{d.name || `Debtor #${d.id}`}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    className={inputClass}
                    value={editForm.title}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, title: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    className={inputClass}
                    value={editForm.status}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}
                  >
                    {[
                      'INTAKE','DEMAND_SENT','NEGOTIATION','LITIGATION','SETTLEMENT','PARTIALLY_RECOVERED','RECOVERED','WRITTEN_OFF','CLOSED'
                    ].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Opened Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={editForm.opened_date || ''}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, opened_date: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  className={inputClass}
                  rows={3}
                  value={editForm.description}
                  onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Principal Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className={inputClass}
                    value={editForm.principal_amount}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, principal_amount: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Currency</label>
                  <input
                    className={inputClass}
                    value={editForm.currency}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, currency: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fee Mode</label>
                  <select
                    className={inputClass}
                    value={editForm.fee_mode}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, fee_mode: e.target.value }))}
                  >
                    <option value="CONTINGENCY_PERCENT">Contingency (% of recovered)</option>
                    <option value="FIXED_AMOUNT">Fixed Amount</option>
                    <option value="NONE">None</option>
                  </select>
                </div>
              </div>

              {editForm.fee_mode === 'CONTINGENCY_PERCENT' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Contingency Percent</label>
                  <input
                    type="number"
                    step="0.01"
                    className={inputClass}
                    value={editForm.contingency_percent}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, contingency_percent: e.target.value }))}
                  />
                </div>
              )}
              {editForm.fee_mode === 'FIXED_AMOUNT' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fixed Fee Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className={inputClass}
                    value={editForm.fixed_fee_amount}
                    onChange={(e) => setEditForm((prev) => ({ ...prev, fixed_fee_amount: e.target.value }))}
                  />
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" className={btnSecondary} onClick={closeEditModal} disabled={editSaving}>Cancel</button>
                <button type="submit" className={btnPrimary} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteOpen && deleteTarget && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-800">Delete Recovery Request</h3>
              <p className="text-xs text-gray-500">{deleteTarget.reference || `DR-${deleteTarget.id}`}</p>
            </div>
            <div className="p-4 text-sm text-gray-700">
              Are you sure you want to delete this debt recovery request? This action cannot be undone.
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className={btnSecondary} onClick={closeDeleteModal} disabled={deleteLoading}>Cancel</button>
              <button className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" onClick={confirmDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {paymentModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Record Recovery Payment</h3>
                <p className="text-xs text-gray-500">{paymentRequest?.reference || (paymentRequest ? `DR-${paymentRequest.id}` : '')}</p>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setPaymentModalOpen(false)} disabled={paymentSaving}>✕</button>
            </div>
            <div className="p-4 space-y-4">
              {paymentError && (
                <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{paymentError}</div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount Recovered *</label>
                  <input
                    type="number"
                    step="0.01"
                    className={paymentError ? inputErrorClass : inputClass}
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))}
                    placeholder="e.g. 1500.00"
                    disabled={paymentSaving}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={paymentForm.payment_date}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, payment_date: e.target.value }))}
                    disabled={paymentSaving}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Method</label>
                  <select
                    className={inputClass}
                    value={paymentForm.method}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, method: e.target.value }))}
                    disabled={paymentSaving}
                  >
                    {['BANK_TRANSFER', 'CASH', 'MOBILE_MONEY', 'CHEQUE', 'OTHER'].map((m) => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reference (optional)</label>
                  <input
                    className={inputClass}
                    value={paymentForm.reference}
                    onChange={(e) => setPaymentForm((p) => ({ ...p, reference: e.target.value }))}
                    placeholder="Bank ref, receipt no, etc"
                    disabled={paymentSaving}
                  />
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Tip: when fees are configured (contingency/fixed), fee WIP is generated automatically and will show under Billing → Unbilled Activity.
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className={btnSecondary} onClick={() => setPaymentModalOpen(false)} disabled={paymentSaving}>Cancel</button>
              <button className={btnPrimary} onClick={submitPayment} disabled={paymentSaving}>{paymentSaving ? 'Saving…' : 'Record Payment'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Debtor Modal */}
      {debtorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Debtor Details</h3>
                <p className="text-xs text-gray-500">{debtorRecord?.name ? debtorRecord.name : debtorRecord?.id ? `Debtor #${debtorRecord.id}` : ''}</p>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setDebtorModalOpen(false)} disabled={debtorSaving}>✕</button>
            </div>
            <div className="p-4">
              {debtorLoading ? (
                <div className="text-sm text-gray-600">Loading…</div>
              ) : (
                <>
                  {debtorError && (
                    <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{debtorError}</div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Name *</label>
                      <input className={inputClass} value={debtorEdit.name} onChange={(e) => setDebtorEdit((p) => ({ ...p, name: e.target.value }))} disabled={debtorSaving} />
                    </div>
                    <div className="flex items-center gap-2 mt-6">
                      <input type="checkbox" checked={!!debtorEdit.is_organization} onChange={(e) => setDebtorEdit((p) => ({ ...p, is_organization: e.target.checked }))} disabled={debtorSaving} />
                      <div className="text-sm text-gray-700">Organization</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <input className={inputClass} value={debtorEdit.phone_number} onChange={(e) => setDebtorEdit((p) => ({ ...p, phone_number: e.target.value }))} disabled={debtorSaving} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <input type="email" className={inputClass} value={debtorEdit.email} onChange={(e) => setDebtorEdit((p) => ({ ...p, email: e.target.value }))} disabled={debtorSaving} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Registration #</label>
                      <input className={inputClass} value={debtorEdit.registration_number} onChange={(e) => setDebtorEdit((p) => ({ ...p, registration_number: e.target.value }))} disabled={debtorSaving} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">National ID</label>
                      <input className={inputClass} value={debtorEdit.national_id} onChange={(e) => setDebtorEdit((p) => ({ ...p, national_id: e.target.value }))} disabled={debtorSaving} />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <textarea className={inputClass} rows={3} value={debtorEdit.address} onChange={(e) => setDebtorEdit((p) => ({ ...p, address: e.target.value }))} disabled={debtorSaving} />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button className={btnSecondary} onClick={() => setDebtorModalOpen(false)} disabled={debtorSaving}>Close</button>
              <button className={btnPrimary} onClick={saveDebtor} disabled={debtorSaving || debtorLoading}>{debtorSaving ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
